import {
  skyMeasuredVelocity,
  skyFireDoppler,
} from '../../../packages/domain/src/110-sky-fire-flow.mjs';

/** Gesture-unlocked, bounded synthesized arcade sound. Simulation counters drive
 * cues; audio owns no game clock, outcome, download, interval or RAF. */
export function createSkyAudio(
  makeContext = () => new (window.AudioContext || window.webkitAudioContext)(),
) {
  let context = null,
    enabled = false,
    dead = false,
    previous = null,
    beat = -1,
    roarBoss = null,
    motion = null,
    breathVoice = null,
    doppler = 1;
  let request = 0;
  const voices = new Set();
  function stop() {
    for (const voice of voices) {
      try {
        voice.stop();
      } catch {
        /* An ended voice may already be disconnected. */
      }
    }
    voices.clear();
    motion = null;
    breathVoice = null;
    doppler = 1;
    beat = -1;
  }
  function note(hz, duration, type = 'triangle', volume = 0.025, delay = 0, end = hz) {
    if (!enabled || dead || context?.state !== 'running' || voices.size >= 16) return;
    const oscillator = context.createOscillator(),
      gain = context.createGain();
    const at = context.currentTime + delay;
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(hz, at);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, end), at + duration);
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.linearRampToValueAtTime(volume, at + 0.006);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    oscillator.connect(gain);
    gain.connect(context.destination);
    voices.add(oscillator);
    oscillator.onended = () => {
      voices.delete(oscillator);
      oscillator.disconnect();
      gain.disconnect();
    };
    oscillator.start(at);
    oscillator.stop(at + duration + 0.01);
  }
  // A short filtered breath under three falling harmonics gives the close
  // approach weight without a sample download or an independent audio clock.
  function roar() {
    if (!enabled || dead || context?.state !== 'running' || voices.size > 12) return;
    const duration = 1.1,
      at = context.currentTime;
    const buffer = context.createBuffer(
      1,
      Math.ceil(context.sampleRate * duration),
      context.sampleRate,
    );
    const data = buffer.getChannelData(0);
    let seed = 1977,
      low = 0;
    for (let index = 0; index < data.length; index++) {
      seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
      low = (low + ((seed / 4294967296) * 2 - 1) * 0.09) / 1.04;
      data[index] = low * (0.75 + 0.25 * Math.sin((index / context.sampleRate) * 47));
    }
    const source = context.createBufferSource(),
      filter = context.createBiquadFilter(),
      gain = context.createGain();
    source.buffer = buffer;
    filter.type = 'bandpass';
    filter.Q.setValueAtTime(0.7, at);
    filter.frequency.setValueAtTime(650, at);
    filter.frequency.exponentialRampToValueAtTime(180, at + duration);
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.linearRampToValueAtTime(0.12, at + 0.12);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    source.connect(filter);
    filter.connect(gain);
    gain.connect(context.destination);
    voices.add(source);
    source.onended = () => {
      voices.delete(source);
      source.disconnect();
      filter.disconnect();
      gain.disconnect();
    };
    source.start(at);
    source.stop(at + duration + 0.01);
    note(92, 1.05, 'sawtooth', 0.025, 0, 38);
    note(67, 1.1, 'triangle', 0.035, 0, 29);
    note(163, 0.85, 'triangle', 0.018, 0.08, 54);
  }
  const breathBuffers = new Map();
  function filteredBurst(kind) {
    if (!enabled || dead || context?.state !== 'running' || voices.size > 13) return;
    const settings = {
      charge: [0.95, 140, 1100, 0.045],
      fire: [0.48, 1800, 220, 0.11],
      breath: [1.35, 1800, 650, 0.075],
      dodge: [0.36, 1300, 160, 0.055],
      steam: [1.15, 2400, 420, 0.045],
      boiler: [1.05, 180, 1300, 0.035],
      cannon: [0.42, 1100, 100, 0.075],
      rupture: [1.5, 2200, 140, 0.08],
    }[kind];
    if (!settings) return;
    const [duration, from, to, volume] = settings,
      at = context.currentTime;
    let buffer = breathBuffers.get(kind);
    if (!buffer) {
      buffer = context.createBuffer(
        1,
        Math.ceil(context.sampleRate * duration),
        context.sampleRate,
      );
      const samples = buffer.getChannelData(0);
      let seed = kind === 'fire' ? 893 : 1977,
        low = 0;
      for (let index = 0; index < samples.length; index++) {
        seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
        const white = seed / 2147483648 - 1;
        low += (white - low) * 0.12;
        samples[index] =
          (white * 0.38 + low * 0.62) *
          (0.72 + 0.28 * Math.sin((index / context.sampleRate) * (kind === 'fire' ? 73 : 31)));
      }
      breathBuffers.set(kind, buffer);
    }
    const source = context.createBufferSource(),
      filter = context.createBiquadFilter(),
      gain = context.createGain();
    source.buffer = buffer;
    if (kind === 'breath') {
      breathVoice = source;
      source.playbackRate?.setValueAtTime(doppler, at);
    }
    filter.type = 'lowpass';
    filter.Q.setValueAtTime(0.65, at);
    filter.frequency.setValueAtTime(from, at);
    filter.frequency.exponentialRampToValueAtTime(to, at + duration);
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.linearRampToValueAtTime(
      volume,
      at + (['charge', 'boiler'].includes(kind) ? 0.65 : 0.025),
    );
    if (kind === 'breath') gain.gain.linearRampToValueAtTime(volume * 0.8, at + 1.1);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    source.connect(filter);
    filter.connect(gain);
    gain.connect(context.destination);
    voices.add(source);
    source.onended = () => {
      if (breathVoice === source) breathVoice = null;
      voices.delete(source);
      source.disconnect();
      filter.disconnect();
      gain.disconnect();
    };
    source.start(at);
    source.stop(at + duration + 0.01);
    if (kind === 'charge') {
      note(72, 0.9, 'triangle', 0.018, 0, 180);
      note(110, 0.8, 'sine', 0.014, 0.05, 290);
    } else if (kind === 'fire') {
      note(96, 0.44, 'sawtooth', 0.025, 0, 30);
      note(58, 0.34, 'sine', 0.035, 0, 24);
    }
  }
  function leviathanCue(kind) {
    if (kind === 'charge') {
      filteredBurst('boiler');
      note(82, 0.95, 'triangle', 0.018, 0, 196);
    } else if (kind === 'cannon') {
      filteredBurst('cannon');
      note(76, 0.45, 'sine', 0.045, 0, 27);
      note(340, 0.16, 'triangle', 0.018, 0.025, 90);
    } else if (kind === 'rupture' || kind === 'impact') {
      filteredBurst('rupture');
      note(52, 1.25, 'sawtooth', 0.022, 0, 24);
      [430, 610, 870].forEach((hz, i) => note(hz, 0.3, 'triangle', 0.012, i * 0.11, hz * 0.45));
    } else if (kind === 'mouth' || kind === 'arrival' || kind === 'crash') {
      filteredBurst('steam');
      note(68, 1.25, 'sine', 0.025, 0, 45);
      note(136, 1.1, 'triangle', 0.012, 0.08, 91);
    }
  }
  function cue(kind) {
    if (kind === 'dragon-roar') roar();
    else if (kind.startsWith('dragon-')) filteredBurst(kind.slice(7));
    else if (kind.startsWith('leviathan-')) leviathanCue(kind.slice(10));
    else if (kind === 'shot') note(980, 0.075, 'triangle', 0.014, 0, 220);
    else if (kind === 'aim-ready') {
      note(660, 0.16, 'sine', 0.022);
      note(990, 0.2, 'sine', 0.022, 0.09);
    } else if (kind === 'critical') {
      note(1320, 0.12, 'sine', 0.025, 0, 1980);
      note(1980, 0.09, 'triangle', 0.014, 0.04);
    } else if (kind === 'armor') note(250, 0.075, 'triangle', 0.018, 0, 95);
    else if (kind === 'overdrive') {
      [660, 990, 1485].forEach((hz, index) => note(hz, 0.22, 'triangle', 0.026, index * 0.055));
    } else if (kind === 'supercharge') {
      [523.25, 783.99, 1046.5, 1567.98].forEach((hz, index) =>
        note(hz, 0.35, 'triangle', 0.025, index * 0.085),
      );
    } else if (kind === 'impact') note(180, 0.065, 'square', 0.009, 0, 65);
    else if (kind === 'kill') {
      note(85, 0.23, 'sawtooth', 0.025, 0, 28);
      note(640, 0.12, 'triangle', 0.028, 0, 160);
    } else if (kind === 'damage') {
      note(140, 0.4, 'sawtooth', 0.04, 0, 35);
      note(740, 0.25, 'square', 0.018, 0, 180);
    } else if (kind === 'nova') {
      note(75, 0.7, 'sawtooth', 0.045, 0, 26);
      note(1600, 0.65, 'triangle', 0.025, 0, 80);
    } else if (kind === 'warning') {
      note(220, 0.18, 'square', 0.018);
      note(330, 0.18, 'square', 0.018, 0.22);
    } else if (kind === 'ring' || kind === 'upgrade' || kind === 'squad') {
      [660, 880, 1320].forEach((hz, i) => note(hz, 0.22, 'sine', 0.035, i * 0.07));
    } else if (kind === 'clear' || kind === 'complete' || kind === 'launch') {
      [261.63, 329.63, 392, 523.25, 783.99].forEach((hz, i) =>
        note(hz, 0.48, 'triangle', 0.035, i * 0.1),
      );
      note(65.4, 0.8, 'sine', 0.035);
    } else if (kind === 'over') {
      [330, 261, 196].forEach((hz, i) => note(hz, 0.5, 'triangle', 0.03, i * 0.18));
    }
  }
  const snapshot = (s) => ({
    shots: s.shotsFired ?? 0,
    hits: s.hitsLanded ?? 0,
    critical: s.criticalHits ?? 0,
    armor: s.armorHits ?? 0,
    overdrive: (s.overdriveTime ?? 0) > 0,
    kills: s.kills,
    lives: s.lives,
    pulse: s.pulse,
    sector: s.sector,
    status: s.status,
    event: s.eventId,
    kind: s.eventKind,
    boss: s.boss?.id,
    aimWindow: s.boss?.aimWindow === true && !s.traversal && !s.boss?.defeated,
    roar:
      s.boss?.artVariant === 'first-dragon' && !s.boss?.defeated && (s.boss?.entryRoar ?? 0) >= 0.5,
    dragon:
      !s.boss?.defeated &&
      (s.boss?.artVariant === 'first-dragon' || ['fireball', 'flame'].includes(s.boss?.attackKind)),
    charge: s.boss?.attackPhase === 'charge' ? (s.boss.charge ?? 0) : 0,
    attack: Number.isFinite(s.boss?.lastAttackAge) ? s.boss.lastAttackAge : -1,
    grazes: s.grazes ?? 0,
    rings: s.ringsCollected ?? 0,
    squads: s.squadsCompleted ?? 0,
    supercharge: s.bossSupercharge?.status ?? null,
    leviathan: s.leviathan?.encounter?.id ?? null,
    leviathanPhase: s.leviathan?.phase ?? null,
    leviathanCharge: s.leviathan?.cue?.charge ?? 0,
    leviathanVolley: s.leviathan?.volley ?? -1,
    leviathanDescent: s.leviathan?.systems?.crashDescent ?? 0,
  });
  function measureSoundMotion(s) {
    const source = s.boss?.anatomy?.attackSocket ?? s.boss?.attackSocket;
    const listener = { x: s.x, y: s.y, z: s.pilotZ ?? 1 };
    const dt = motion && motion.boss === s.boss?.id ? s.time - motion.time : 0;
    if (s.status !== 'playing' || s.traversal || !source) {
      motion = null;
      doppler = 1;
      return;
    }
    if (dt > 0) {
      doppler = skyFireDoppler(
        source,
        listener,
        skyMeasuredVelocity(motion.source, source, dt),
        skyMeasuredVelocity(motion.listener, listener, dt),
      );
      if (enabled && context?.state === 'running')
        breathVoice?.playbackRate?.setTargetAtTime?.(doppler, context.currentTime, 0.04);
    }
    motion = { boss: s.boss?.id, time: s.time, source: { ...source }, listener };
  }
  function sync(s) {
    motion = null;
    doppler = 1;
    measureSoundMotion(s);
    previous = snapshot(s);
    if (previous.roar) roarBoss = previous.boss;
    else if (!previous.boss) roarBoss = null;
  }
  function update(s) {
    measureSoundMotion(s);
    const next = snapshot(s),
      old = previous;
    previous = next;
    if (!old) return;
    if (next.boss !== old.boss) roarBoss = null;
    if (next.status === 'playing' && next.leviathan !== null) {
      if (next.leviathanPhase !== old.leviathanPhase) {
        const phaseCue = {
          arrival: 'arrival',
          entry: 'mouth',
          breakout: 'rupture',
          crash: 'crash',
        }[next.leviathanPhase];
        if (phaseCue) cue(`leviathan-${phaseCue}`);
      }
      if (next.leviathanCharge >= 0.12 && old.leviathanCharge < 0.12) cue('leviathan-charge');
      if (next.leviathanVolley > old.leviathanVolley && next.leviathanPhase === old.leviathanPhase)
        cue('leviathan-cannon');
      if (next.leviathanDescent >= 0.95 && old.leviathanDescent < 0.95) cue('leviathan-impact');
    }
    if (next.status === 'playing' && next.aimWindow && !old.aimWindow) cue('aim-ready');
    if (next.status === 'playing' && next.roar && !old.roar && roarBoss !== next.boss) {
      roarBoss = next.boss;
      cue('dragon-roar');
    }
    if (next.status === 'playing' && next.dragon && next.boss === old.boss) {
      if (next.charge >= 0.15 && old.charge < 0.15) cue('dragon-charge');
      if (next.attack > old.attack && s.boss.age - next.attack < 0.2)
        cue(s.boss.breath?.active ? 'dragon-breath' : 'dragon-fire');
    }
    if (next.status === 'playing' && next.grazes > old.grazes) cue('dragon-dodge');
    if (next.status !== old.status && ['intermission', 'complete', 'over'].includes(next.status)) {
      stop();
      cue(next.status === 'intermission' ? 'clear' : next.status);
      return;
    }
    if (next.critical > old.critical) cue('critical');
    else if (next.armor > old.armor) cue('armor');
    if (next.overdrive && !old.overdrive) cue('overdrive');
    if (next.lives < old.lives) cue('damage');
    else if (next.kills > old.kills) cue('kill');
    else if (next.hits > old.hits && next.critical === old.critical && next.armor === old.armor)
      cue('impact');
    if (next.supercharge === 'ready' && old.supercharge !== 'ready') cue('supercharge');
    else if (next.squads > old.squads) cue('squad');
    if (next.rings > old.rings) cue('ring');
    if (next.pulse < old.pulse) cue('nova');
    if (next.boss && next.boss !== old.boss) cue('warning');
    if (next.shots > old.shots) cue('shot');
    if (s.status !== 'playing') return;
    const currentBeat = Math.floor(s.time * 3.6);
    if (currentBeat !== beat) {
      beat = currentBeat;
      const root = [65.41, 77.78, 58.27, 69.3][s.sector % 4];
      note(root * [1, 1, 1.5, 2, 1, 1.25, 1.5, 1][beat % 8], 0.18, 'triangle', 0.009);
      if (beat % 2 === 0) note(110, 0.12, 'sine', 0.02, 0, 35);
    }
  }
  return {
    sync,
    update,
    cue,
    stop,
    async enable(value) {
      const epoch = ++request;
      enabled = Boolean(value) && !dead;
      if (!enabled) {
        stop();
        return false;
      }
      try {
        context ??= makeContext();
        await context.resume();
        if (epoch !== request || dead) return false;
        enabled = context.state === 'running';
      } catch {
        if (epoch === request) enabled = false;
      }
      return epoch === request && enabled && !dead;
    },
    async resume() {
      if (!enabled || dead) return false;
      try {
        await context?.resume();
      } catch {
        return false;
      }
      return enabled && !dead && context?.state === 'running';
    },
    dispose() {
      request++;
      dead = true;
      enabled = false;
      stop();
      breathBuffers.clear();
      context?.close().catch(() => {});
    },
  };
}
